<template>
  <v-container class="ma-0 pa-0">
    <v-card :loading="store.loading_data" flat :style="{ maxHeight: '80vh', overflowY: 'auto' }">
      <v-form @submit.prevent="submit" @reset="reset_form">
        <v-dialog
          persistent
          v-model="warning_dialog"
          transition="slide-y-transition"
          width="100%"
        >
          <div :style="{display: 'flex', justifyContent: 'center', alignItems: 'center'}">
            <v-card>
              <v-card-title style="color: red">
                {{ t('warning') }}
              </v-card-title>
              <v-card-text>
                <h3>{{ t('warning_message') }}</h3>
              </v-card-text>
              <v-card-actions>
                <template v-slot:default>
                  <v-btn
                    color="secondary"
                    type="reset"
                    @click="close_form"
                  >
                    {{ t('cancel') }}
                  </v-btn>
                  <v-spacer/>
                  <v-btn
                    color="red"
                    @click="submit"
                  >
                    {{ t('submit') }}
                  </v-btn>
                </template>
              </v-card-actions>
            </v-card>
          </div>
        </v-dialog>
        <v-card-title class="text-h5">
          {{ ticket === null ? `${t('new_issue')}` : `${t('edit_issue')}: ${ticket.id} ` }}
        </v-card-title>
        <v-card-text>
          <v-row>
            <!-- Park Dropdown -->
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="form.park_id.value.value"
                :error-messages="form.park_id.errorMessage.value"
                :items="store.park_list"
                item-title="name"
                item-value="id"
                :label="t('park_name')"
                @update:modelValue="update_park_info"
                :disabled="ticket !== null"
              />
            </v-col>

            <!-- Priority Dropdown -->
            <v-col cols="12" md="2" class="d-flex justify-center">
              <v-select
                :items="priorities"
                rounded
                item-title="text"
                item-value="value"
                variant="solo"
                :label="t('priority')"
                v-model="form.priority.value.value"
                :error="form.priority.value.value === null"
              />
            </v-col>

            <!-- Problem Date Picker -->
            <v-col cols="12" md="2" class="d-flex align-center">
              <v-dialog :close-on-content-click="false" width="unset">
                <template v-slot:activator="{props}">
                  <v-text-field
                    readonly
                    v-model="problem_shown_date"
                    v-bind="props"
                    :label="t('problem_date')"
                    prepend-inner-icon="mdi-calendar"
                    :error-messages="form.problem_date.errorMessage.value"
                  />
                </template>
                <template v-slot:default="{isActive}">
                  <v-card>
                    <v-date-picker
                      color="primary"
                      v-model="form.problem_date.value.value"
                      @update:modelValue="isActive.value = false"
                    />
                  </v-card>
                </template>
              </v-dialog>
            </v-col>

            <!-- Creator of Issue -->
            <v-col cols="12" md="2">
              <v-autocomplete
                v-model="form.creator.value.value"
                :error-messages="form.creator.errorMessage.value"
                :items="store.staffers"
                item-title="username"
                item-value="id"
                :label="t('creator')"
              />
            </v-col>

            <!-- Checkbox -->
            <v-col cols="12" md="2" class="d-flex justify-center">
              <v-checkbox v-model="form.is_solved.value.value" :label="t('is_solved')"/>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                :label="t('title')"
                v-model="form.title.value.value"
                :error-messages="form.title.errorMessage.value"
              />
            </v-col>
            <!-- Comment Text Box -->
            <v-col cols="12" md="4">
              <v-textarea
                v-model="form.comment.value.value"
                :error-messages="form.comment.errorMessage.value"
                :label="t('comment')"
                rows="4"
                variant="solo-filled"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-textarea
                v-model="form.suggestion.value.value"
                :label="t('suggestion')"
                rows="4"
                variant="solo-filled"
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- Intervention Date Picker -->
            <v-col cols="12" md="4">
              <v-dialog :close-on-content-click="false" width="unset">
                <template v-slot:activator="{props}">
                  <v-text-field
                    disabled
                    readonly
                    v-model="intervention_shown_date"
                    v-bind="props"
                    :label="t('intervention_date')"
                    prepend-inner-icon="mdi-calendar"
                    :error-messages="form.intervention_date.errorMessage.value"
                  />
                </template>
                <template v-slot:default="{isActive}">
                  <v-card>
                    <v-date-picker
                      color="primary"
                      v-model="form.intervention_date.value.value"
                      @update:modelValue="isActive.value = false"
                    />
                  </v-card>
                </template>
              </v-dialog>
            </v-col>

            <!-- Agency Dropdown -->
            <v-col cols="12" md="4">
              <v-text-field
                readonly
                v-model="agency"
                :label="t('agency')"
                variant="underlined"
              />
            </v-col>

            <!-- Address -->
            <v-col cols="12" md="3">
              <v-text-field v-model="park_address" :label="t('address')" readonly variant="underlined"></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions
          class="justify-end"
        >
          <v-btn
            color="secondary"
            type="reset"
            @click="close_form"
          >
            {{ t('cancel') }}
          </v-btn>
          <v-spacer/>
          <v-btn
            color="primary"
            type="submit"
            :disabled="form.priority.value.value === null"
          >
            {{ t('submit') }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import { useIssueStore } from "../store/issues_store.js";
import { useAuthStore } from "../store/authstore.js";
import axios from "axios";
import { endpoint, getFormFromSchema } from "../issues_utils.js";
import { useForm } from "vee-validate";
import * as yup from 'yup';
import { useI18n } from "vue-i18n";

const emit = defineEmits(['close-form']);
const close_form = () => {
  warning_dialog.value = false;
  warning_confirmation.value = false;
  emit('close-form');
};

const props = defineProps({
  ticket: {
    type: [Object, null],
    required: true
  }
});

const { t } = useI18n({ inheritLocale: true, useScope: 'local' });

const store = useIssueStore();
const auth_store = useAuthStore();

const park_address = ref('');
const agency = ref('');

const priority = ref({ value: 0, text: 'Select Priority', color: 'grey' });
const priorities = ref([
  { value: 1, text: 'High', color: 'red' },
  { value: 2, text: 'Medium', color: 'orange' },
  { value: 3, text: 'Low', color: 'green' }
]);

const problem_shown_date = ref('');
const intervention_shown_date = ref('');
const update_park_info = async () => {
  if (form.park_id.value.value) {
    try {
      const park_res = await axios.get(endpoint(`internal/current/parks/${form.park_id.value.value}/`),
        { withCredentials: true });
      if (park_res.statusText === "OK") {
        park_address.value = park_res.data['address'];
        const agency_id = park_res.data['agency'];
        if (agency_id) {
          agency.value = store.agency_list.find(a => a.id === agency_id)['name'];
        } else {
          agency.value = 'Not assigned';
        }
      }
    } catch (e) {
      alert(e.message);
    }
  }
};

const schema = yup.object({
  park_id: yup.number().required(),
  priority: yup.number().required().min(1),
  problem_date: yup.date().required(),
  creator: yup.number().required(),
  title: yup.string().required(),
  comment: yup.string().required(),
  suggestion: yup.string(),
  is_solved: yup.boolean().required(),
  intervention_date: yup.date(),
});

let default_values = {
  park_id: null,
  creator: auth_store['user']['id'],
  priority: null,
  problem_date: null,
  intervention_date: new Date(),
  title: '',
  comment: '',
  suggestion: '',
  is_solved: false,
};

let { handleSubmit, resetForm } = useForm({
  validationSchema: schema,
  initialValues: default_values
});

const form = getFormFromSchema(schema);

const reset_form = () => {
  resetForm();
  priority.value = { value: 0, text: 'Select Priority', color: 'grey' };
  problem_shown_date.value = '';
  intervention_shown_date.value = '';
  agency.value = null;
  park_address.value = null;
};

const warning_dialog = ref(false);
const warning_confirmation = ref(false);
const existing_warning = ref(false);
watch(form.park_id.value, (newVal) => {
  if (props.ticket === null) {
    const ticket = store.ticket_list.filter(t => !t.is_solved).find(t => t.park.id === newVal);
    existing_warning.value = !!ticket;
  }
});

const submit = handleSubmit(async (values) => {
  if (existing_warning.value && !warning_confirmation.value && props.ticket === null) {
    warning_confirmation.value = true;
    warning_dialog.value = true;
    return;
  }

  const body = {
    'park': values.park_id,
    'priority': values.priority,
    'start_date': values.problem_date.toLocaleDateString('sv'),
    'made_by': values.creator,
    'title': values.title,
    'issue_descr': values.comment,
    'how_to_solve_descr': values.suggestion,
    'is_solved': values.is_solved,
    // 'resolution_date': values.intervention_date.toLocaleDateString('sv'),
  };
  try {
    if (props.ticket === null) {
      const response = await axios.post(endpoint('internal/current/tickets/'), body, { withCredentials: true });
      if (response.statusText === "Created") {
        reset_form();
        alert('Ticket sent.');
        close_form();
      }
    } else {
      const response = await axios.put(endpoint(`internal/current/tickets/${props.ticket['id']}/`), body, { withCredentials: true });
      if (response.status === 200) {
        alert('Ticket updated.');
        close_form();
      }
    }
  } catch (e) {
    alert(e.toString());
  } finally {
    store.ticket_list = [];
    await store.load_tickets();
  }
});

watch(form.problem_date.value, (newVal) => {
  problem_shown_date.value = new Date(newVal).toLocaleDateString('it-IT');
});

watch(form.intervention_date.value, (newVal) => {
  intervention_shown_date.value = new Date(newVal).toLocaleDateString('it-IT');
});

onMounted(async () => {
  if (props.ticket !== null) {
    priority.value = priorities.value.find(p => p['value'] === props.ticket['priority']);
    problem_shown_date.value = new Date(props.ticket['start_date']).toLocaleDateString('it-IT');
    intervention_shown_date.value = new Date(props.ticket['resolution_date']).toLocaleDateString('it-IT');
    park_address.value = props.ticket['park']['address'];
    const agency_id = props.ticket['park']['agency'];
    if (agency_id) {
      agency.value = store.agency_list.find(a => a.id === agency_id)['name'];
    } else {
      agency.value = 'Not assigned';
    }

    form.park_id.value.value = props.ticket['park']['id'];
    form.creator.value.value = props.ticket['made_by'];
    form.priority.value.value = props.ticket['priority'];
    form.problem_date.value.value = new Date(props.ticket['start_date']);
    // form.intervention_date.value.value = new Date(props.ticket['resolution_date']);
    form.intervention_date.value.value = new Date();
    form.title.value.value = props.ticket['title'];
    form.comment.value.value = props.ticket['issue_descr'];
    form.suggestion.value.value = props.ticket['how_to_solve_descr'];
    form.is_solved.value.value = props.ticket['is_solved'];
  }
});

</script>

<i18n>
{
  "en": {
    "new_issue": "New Issue",
    "edit_issue": "Edit Issue",
    "park_name": "Park Name",
    "priority": "Priority",
    "problem_date": "Issue started on",
    "creator": "Creator",
    "is_solved": "Solved?",
    "title": "Title of the issue",
    "comment": "Description of the issue",
    "suggestion": "How to solve the issue",
    "intervention_date": "Last maintenance date",
    "agency": "Agency",
    "address": "Address of the park",
    "cancel": "Cancel",
    "submit": "Submit",
    "warning": "Warning!",
    "warning_message": "The selected park has already an active ticket, are you sure you want to create a new one?"
  },
  "it": {
    "new_issue": "Nuovo ticket",
    "edit_issue": "Modifica ticket",
    "park_name": "Nome parco",
    "priority": "Priorità",
    "problem_date": "Il problema è stato iniziato il",
    "creator": "Creatore",
    "is_solved": "Risolto?",
    "title": "Titolo del ticket",
    "comment": "Descrizione del problema",
    "suggestion": "Come risolvere il problema",
    "intervention_date": "Data ultima manutenzione",
    "agency": "Agenzia",
    "address": "Indirizzo del parco",
    "cancel": "Annulla",
    "submit": "Invia",
    "warning": "Attenzione!",
    "warning_message": "Il parco selezionato ha già un ticket attivo, sei sicuro di volerne creare uno nuovo?"
  },
  "fr": {
    "new_issue": "Nouveau problème",
    "edit_issue": "Modifier problème",
    "park_name": "Nom du parc",
    "priority": "Priorité",
    "problem_date": "Il problème a commencé le",
    "creator": "Créateur",
    "is_solved": "Est-ce résolu?",
    "title": "Titre du problème",
    "comment": "Description du problème",
    "suggestion": "Comment résoudre le problème",
    "intervention_date": "Date de la dernière maintenance",
    "agency": "Agence",
    "address": "Adresse du parc",
    "cancel": "Annulerer",
    "submit": "Envoyer",
    "warning": "Attention !",
    "warning_message": "Le parc sélectionné a déjà un ticket actif, êtes-vous sûr de vouloir en créer un nouveau ?"
  },
  "nl": {
    "new_issue": "Nieuw probleem",
    "edit_issue": "Bewerk probleem",
    "park_name": "Parkeernaam",
    "priority": "Prioriteit",
    "problem_date": "Het probleem begon op",
    "creator": "Auteur",
    "is_solved": "Is dit opgelost?",
    "title": "Titel van het probleem",
    "comment": "Beschrijving van het probleem",
    "suggestion": "Hoe te verhelpen met het probleem",
    "intervention_date": "Datum van de laatste interventie",
    "agency": "Organisatie",
    "address": "Adres van de parkeerplaats",
    "cancel": "Annuleren",
    "submit": "Versturen",
    "warning": "Attentie!",
    "warning_message": "Het geselecteerde park heeft al een actieve kwestie, weet je zeker dat je een nieuwe wilt aanmaken?"
  }
}
</i18n>

<style scoped>

</style>
